import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { DocumentSnapshot } from "firebase/firestore";
import { FiFilm, FiHardDrive, FiHome, FiUploadCloud } from "react-icons/fi";
import type { Clip, ClipSort } from "../types";
import {
  CLIPS_PER_PAGE,
  backfillLegacyClipMetadata,
  deleteClipFully,
  getBangeredClipIds,
  getClipCount,
  getClipById,
  getClipPage,
  getTotalClipSize,
  markClipAsBanger,
} from "../services/clipService";
import { GalleryGrid } from "../components/GalleryGrid";
import { Pagination } from "../components/Pagination";
import { SortDropdown } from "../components/SortDropdown";
import { VideoModal } from "../components/VideoModal";
import { AuthButton } from "../components/AuthButton";
import { ToastRegion } from "../components/ToastRegion";
import { ThemeToggle } from "../components/ThemeToggle";
import { useAuthUser } from "../hooks/useAuthUser";
import { useClipTheme } from "../hooks/useClipTheme";
import { useToasts } from "../hooks/useToasts";
import { formatFileSize } from "../utils";
import "../clips.css";

export default function ClipsPage() {
  const { user } = useAuthUser();
  const { theme, toggleTheme } = useClipTheme();
  const { toasts, addToast, dismissToast } = useToasts();
  const [clips, setClips] = useState<Clip[]>([]);
  const [selectedClip, setSelectedClip] = useState<Clip | null>(null);
  const [sort, setSort] = useState<ClipSort>("uploaded-desc");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [totalSizeBytes, setTotalSizeBytes] = useState(0);
  const [bangeredIds, setBangeredIds] = useState<Set<string>>(new Set());
  const [bangerBusyIds, setBangerBusyIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const cursorCache = useRef(new Map<ClipSort, Map<number, DocumentSnapshot | null>>());
  const requestId = useRef(0);

  useEffect(() => {
    setPage(1);
  }, [sort]);

  useEffect(() => {
    const sharedClipId = new URLSearchParams(window.location.search).get("clip");
    if (!sharedClipId) return;
    void getClipById(sharedClipId)
      .then((clip) => {
        if (clip) setSelectedClip(clip);
      })
      .catch((error) => console.error("Could not open shared clip", error));
  }, []);

  useEffect(() => {
    const currentRequest = ++requestId.current;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        let sortCursors = cursorCache.current.get(sort);
        if (!sortCursors) {
          sortCursors = new Map([[1, null]]);
          cursorCache.current.set(sort, sortCursors);
        }

        if (!sortCursors.has(page)) {
          const knownPages = Array.from(sortCursors.keys()).filter((knownPage) => knownPage < page);
          let knownPage = Math.max(...knownPages);
          while (knownPage < page) {
            const intermediate = await getClipPage(sort, sortCursors.get(knownPage) ?? null);
            if (!intermediate.lastDocument) break;
            sortCursors.set(knownPage + 1, intermediate.lastDocument);
            knownPage += 1;
          }
        }

        const [result, count, totalSize] = await Promise.all([
          getClipPage(sort, sortCursors.get(page) ?? null),
          getClipCount(),
          getTotalClipSize(),
        ]);
        if (!cancelled && currentRequest === requestId.current) {
          setClips(result.clips);
          setTotalSizeBytes(totalSize);
          setTotalPages(Math.max(1, Math.ceil(count / CLIPS_PER_PAGE)));
          if (result.lastDocument) sortCursors.set(page + 1, result.lastDocument);
        }
      } catch (loadError) {
        if (!cancelled && currentRequest === requestId.current) {
          setClips([]);
          setTotalSizeBytes(0);
          console.error("Clip gallery query failed", loadError);
        }
      } finally {
        if (!cancelled && currentRequest === requestId.current) setLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [page, sort]);

  useEffect(() => {
    let cancelled = false;
    if (!user || clips.length === 0) {
      setBangeredIds(new Set());
      return () => { cancelled = true; };
    }

    void getBangeredClipIds(user.uid, clips.map((clip) => clip.id))
      .then((ids) => {
        if (!cancelled) setBangeredIds(ids);
      })
      .catch((error) => {
        console.error("Could not load Banger status", error);
        if (!cancelled) setBangeredIds(new Set());
      });

    return () => { cancelled = true; };
  }, [clips, user]);

  useEffect(() => {
    const signedInName = user?.email?.split("@")[0];
    if (!signedInName) return;
    const fillOwnLegacyName = (clip: Clip) => clip.uploadedBy === user.uid && clip.uploaderName === "Player"
      ? { ...clip, uploaderName: signedInName }
      : clip;
    setClips((current) => current.map(fillOwnLegacyName));
    setSelectedClip((current) => current ? fillOwnLegacyName(current) : current);
  }, [user]);

  useEffect(() => {
    const signedInName = user?.email?.split("@")[0];
    if (!signedInName) return;
    const legacyClips = clips.filter((clip) => clip.uploadedBy === user.uid && clip.needsMetadataBackfill);
    if (legacyClips.length === 0) return;

    setClips((current) => current.map((clip) => legacyClips.some((legacy) => legacy.id === clip.id)
      ? { ...clip, uploaderName: signedInName, bangerCount: 0, needsMetadataBackfill: false }
      : clip));
    void Promise.allSettled(legacyClips.map((clip) => backfillLegacyClipMetadata(clip.id, signedInName)))
      .then((results) => {
        results.forEach((result) => {
          if (result.status === "rejected") console.error("Could not update legacy clip metadata", result.reason);
        });
      });
  }, [clips, user]);

  const handleBanger = async (clip: Clip) => {
    if (!user) {
      addToast("Sign in to mark clips as Bangers.", "info");
      return;
    }
    if (bangeredIds.has(clip.id) || bangerBusyIds.has(clip.id)) return;

    setBangerBusyIds((current) => new Set(current).add(clip.id));
    try {
      const marked = await markClipAsBanger(clip.id, user.uid);
      setBangeredIds((current) => new Set(current).add(clip.id));

      if (marked) {
        const updateCount = (item: Clip) => item.id === clip.id
          ? { ...item, bangerCount: item.bangerCount + 1 }
          : item;
        setClips((current) => {
          const updated = current.map(updateCount);
          return sort === "bangers-desc"
            ? updated.sort((left, right) => right.bangerCount - left.bangerCount)
            : updated;
        });
        setSelectedClip((current) => current ? updateCount(current) : current);
        addToast("Marked as a Banger! 🔥", "success");
      } else {
        addToast("You already marked this clip as a Banger.", "info");
      }
    } catch (error) {
      console.error("Could not mark clip as a Banger", error);
      addToast("Could not mark this Banger. Please try again.", "error");
    } finally {
      setBangerBusyIds((current) => {
        const next = new Set(current);
        next.delete(clip.id);
        return next;
      });
    }
  };

  const isAdmin = user?.email?.toLowerCase() === "splatoonman723@gmail.com";

  const handleDelete = async (clip: Clip) => {
    if (!isAdmin || deletingIds.has(clip.id)) return;
    const confirmed = window.confirm(`Permanently delete “${clip.title}”? This removes the video, thumbnail, Firestore record, and all Bangers.`);
    if (!confirmed) return;

    setDeletingIds((current) => new Set(current).add(clip.id));
    try {
      await deleteClipFully(clip);
      setClips((current) => current.filter((item) => item.id !== clip.id));
      setSelectedClip((current) => current?.id === clip.id ? null : current);
      setBangeredIds((current) => {
        const next = new Set(current);
        next.delete(clip.id);
        return next;
      });
      cursorCache.current.clear();
      const remainingCount = await getClipCount();
      setTotalSizeBytes((current) => Math.max(0, current - clip.sizeBytes));
      const nextTotalPages = Math.max(1, Math.ceil(remainingCount / CLIPS_PER_PAGE));
      setTotalPages(nextTotalPages);
      if (page > nextTotalPages) setPage(nextTotalPages);
      addToast("Clip permanently deleted.", "success");
    } catch (error) {
      console.error("Could not fully delete clip", error);
      addToast("Could not delete this clip. Please try again.", "error");
    } finally {
      setDeletingIds((current) => {
        const next = new Set(current);
        next.delete(clip.id);
        return next;
      });
    }
  };

  const handleShare = async (clip: Clip) => {
    try {
      const shareUrl = `${window.location.origin}/clips/share/${encodeURIComponent(clip.id)}?b=${clip.bangerCount}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const input = document.createElement("textarea");
        input.value = shareUrl;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        const copied = document.execCommand("copy");
        input.remove();
        if (!copied) throw new Error("Copy command failed");
      }
      addToast("Discord embed link copied.", "success");
    } catch (error) {
      console.error("Could not copy clip link", error);
      addToast("Could not copy the video link. Please try again.", "error");
    }
  };

  const closeSelectedClip = () => {
    setSelectedClip(null);
    const url = new URL(window.location.href);
    if (url.searchParams.has("clip")) {
      url.searchParams.delete("clip");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  };

  const isEmpty = !loading && clips.length === 0;
  return (
    <main className="clips-app" data-clip-theme={theme}>
      <header className="clips-header">
        <Link to="/clips" className="clips-brand"><span><FiFilm /></span><div><b>NOVA GANG</b><small>CLIPS</small></div></Link>
        <nav aria-label="Clips navigation">
          <Link to="/"><FiHome /> Home</Link>
          <Link className="active" to="/clips">Discover</Link>
          <Link to="/clips/upload"><FiUploadCloud /> Upload</Link>
        </nav>
        <div className="clip-header-actions">
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          <AuthButton user={user} onError={(message) => addToast(message, "error")} />
        </div>
      </header>

      <section className="clips-content">
        <div className="clips-title-row">
          <h1>Clips</h1>
          <Link className="clip-primary-cta" to="/clips/upload"><FiUploadCloud /> Upload clips</Link>
        </div>

        <div className="clips-toolbar">
          {!loading && clips.length > 0 && (
            <div className="clip-total-size" aria-label={`Total clip size ${formatFileSize(totalSizeBytes)}`}>
              <span className="clip-total-size-icon"><FiHardDrive aria-hidden="true" /></span>
              <span><small>Total Size</small><strong>{formatFileSize(totalSizeBytes)}</strong></span>
            </div>
          )}
          <SortDropdown value={sort} onChange={setSort} />
        </div>

        <GalleryGrid
          clips={clips}
          loading={loading}
          bangeredIds={bangeredIds}
          bangerBusyIds={bangerBusyIds}
          isAdmin={isAdmin}
          deletingIds={deletingIds}
          onOpen={setSelectedClip}
          onBanger={(clip) => { void handleBanger(clip); }}
          onDelete={(clip) => { void handleDelete(clip); }}
          onShare={(clip) => { void handleShare(clip); }}
        />
        {isEmpty && (
          <div className="clip-empty-state">
            <span><FiFilm /></span>
            <h2>No clips yet</h2>
          </div>
        )}
        {!isEmpty && <Pagination page={page} totalPages={totalPages} onChange={setPage} disabled={loading} />}
      </section>

      {selectedClip && (
        <VideoModal
          clip={selectedClip}
          bangered={bangeredIds.has(selectedClip.id)}
          bangerBusy={bangerBusyIds.has(selectedClip.id)}
          isAdmin={isAdmin}
          deleting={deletingIds.has(selectedClip.id)}
          onClose={closeSelectedClip}
          onBanger={(clip) => { void handleBanger(clip); }}
          onDelete={(clip) => { void handleDelete(clip); }}
          onShare={(clip) => { void handleShare(clip); }}
        />
      )}
      <ToastRegion toasts={toasts} onDismiss={dismissToast} />
    </main>
  );
}
